import { NgClass } from "@angular/common";
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthPermIdentifier } from "@myapp/modules/fwcore/models";
import { RequestUserPermission } from "@myapp/modules/fwcore/models/messages";
import { AuthorizationService } from "@myapp/modules/fwcore/services";
import { ControlResizerOptions, ControlResizerService, IsgHelperService } from "@mysrc/services/framework";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthTreeComponent } from "../../components/auth/auth-tree/auth-tree.component";
import { ModulesListComponent } from "../../components/modules/modules-list/modules-list.component";
import { RolesListComponent } from "../../components/roles/roles-list/roles-list.component";
import { UserListComponent } from "../../components/users/user-list/user-list.component";
import { ICompany, Module, RequestLicense, Role } from "../../models";
import { AdminService } from "../../services";

@Component({
  selector: 'app-page-license-roles',
  standalone: true,
  templateUrl: './page-license-roles.component.html',
  styleUrls: ['./page-license-roles.component.scss'],
  imports: [RouterLink, NgClass, ModulesListComponent, RolesListComponent, AuthTreeComponent, UserListComponent]
})
export class PageLicenseRolesComponent implements AfterViewChecked, OnInit, OnDestroy, AfterViewInit {

  /*-------------------------- public properties / methods --------------------*/

  public currentLicenseId: string | null = null;

  public currentModuleId!: string;

  public currentRoleId: string | undefined | null = null;

  public companyName: string | undefined | null = null;

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(ModulesListComponent)
  protected moduleListComponent!: ModulesListComponent;

  @ViewChild(RolesListComponent)
  protected rolesListComponent!: RolesListComponent;

  @ViewChild('authtreevisual')
  protected authTreeVisualComponent!: AuthTreeComponent;

  @ViewChild('authtreedata')
  protected authTreeDataComponent!: AuthTreeComponent;

  @ViewChild(UserListComponent)
  protected userListComponent!: UserListComponent;


  protected companies: ICompany[] | undefined;

  protected destroy$ = new Subject();

  protected height!: number;

  protected viewInit: boolean = false;

  protected afterViewInit: boolean = false;

  protected saveRolePermissions: boolean | null = null;

  protected hideModule: boolean = false;

  protected hideAuthDataTree: boolean = false;

  /*-------------------------- State  --------------------------*/

  protected reqPerm: RequestUserPermission = {
  }

  protected perms?: AuthPermIdentifier[] = [];


  protected hasRoleSave: boolean = false;
  protected hasRoleDelete: boolean = false;
  protected hasRoleDetail: boolean = false;
  protected hasRoleTemplate: boolean = false;

  protected hasPermCreate: boolean = false;
  protected hasPermEdit: boolean = false;
  protected hasPermDetail: boolean = false;
  protected hasPermReOrder: boolean = false;
  protected hasPermDelete: boolean = false;

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected controlResizer: ControlResizerService
    , protected cdr: ChangeDetectorRef
    , protected hlp: IsgHelperService) {

  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {
          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) { this.router.navigate(['./admin/license']); }

          this.updateCompanyInfo();

        });

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );

    this.reqPerm.hierarchyCodes = ['roles', 'permissions'];

    this.serviceAuth.pagePermissionsGet$(this.reqPerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.perms = resp.data ?? [];

        this.hasRoleSave = this.perms?.some(i => i.code === "role-set") ?? false;
        this.hasRoleDetail = this.perms?.some(i => i.code === "role-detail") ?? false;
        this.hasRoleDelete = this.perms?.some(i => i.code === "role-delete") ?? false;
        this.hasRoleTemplate = this.perms?.some(i => i.code === "role-template-set") ?? false;

        this.hasPermCreate = this.perms?.some(i => i.code === "perm-create") ?? false;
        this.hasPermEdit = this.perms?.some(i => i.code === "perm-edit") ?? false;
        this.hasPermDetail = this.perms?.some(i => i.code === "perm-detail") ?? false;
        this.hasPermReOrder = this.perms?.some(i => i.code === "perm-reorder") ?? false;
        this.hasPermDelete = this.perms?.some(i => i.code === "perm-delete") ?? false;

      });

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewChecked(): void {

    this.sizeChange();
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {

    this.afterViewInit = true;
    this.moduleListComponent.Initialized();
  }

  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();

  }

  /*--------------------------- event handlers  --------------------------*/

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.sizeChange();
  }

  protected onModuleSelected(ev: Module | null) {
    if (ev?.id) {
      this.currentModuleId = ev.id;
    }
  }

  protected onModulesLoaded(count: number | null) {
    let v = !!(count) ? (count < 2) : true;
    this.hideModule = v;
  }

  protected onRoleSelected(role: Role | null) {

    if (role?.id)
      this.userListComponent.Initialized();

    this.currentRoleId = role?.id;
  }

  protected onSaveRolePermissionsClicked(isSave: boolean) {
    if (isSave) {
      this.saveRolePermissions = isSave;
    }
  }

  protected onDataTreeLoaded(count: number | null) {
    let v = !!(count) ? (count < 1) : true;
    this.hideAuthDataTree = v;
  }

  protected onNavigateToLicense(): void {
    this.router.navigate(['/admin/license'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToRole(): void {
    this.router.navigate(['/admin/role'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToUserManagement(): void {
    this.router.navigate(['/admin/user/license'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToUserPermission(): void {
    this.router.navigate(['/admin/user/permission'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  /*--------------------------- internal functions  --------------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 54;
      options.offsetBottomHeight = 21;

      this.height = this.controlResizer.calculateHeight(options);

      options.offsetTopHeight = 6;
      options.offsetBottomHeight = 29;
      this.sizeChangeSiblings(options);
    }

  }

  protected sizeChangeSiblings(options: ControlResizerOptions) {

    if (this.moduleListComponent && this.moduleListComponent.active)
      this.moduleListComponent.heightChange(options);

    if (this.rolesListComponent)
      this.rolesListComponent.heightChange(options);

    if (this.authTreeVisualComponent)
      this.authTreeVisualComponent.heightChange(options);

    if (this.authTreeDataComponent)
      this.authTreeDataComponent.heightChange(options);

    if (this.userListComponent)
      this.userListComponent.heightChange(options);

  }

  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {
        if (resp.data && resp.data.length === 1)
          this.companyName = resp.data[0].companyName;
        else
          this.companyName = "";
      }
    );
  }

}



